import { clsx, type ClassValue } from 'clsx'
import dayjs from 'dayjs'
import { twMerge } from 'tailwind-merge'
import { v4 as uuidv4 } from 'uuid'
import { pulsechain } from 'viem/chains'


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export default function calculateTimeAgo(date: any) {
  const now = dayjs()
  const then = dayjs(date)
  const diff = now.diff(then, 'day')
  if (diff > 0) {
    return `${diff}d`
  }

  const diffHour = now.diff(then, 'hour')
  if (diffHour > 0) {
    return `${diffHour}h`
  }

  const diffMinute = now.diff(then, 'minute')
  if (diffMinute > 0) {
    return `${diffMinute}m`
  }

  return `${now.diff(then, 'second')}s`
}

export const _log = (...message: any[]) => {
  if ([process.env.NODE_ENV, process.env.VERCEL_ENV, process.env.NEXT_PUBLIC_VERCEL_ENV].includes('production')) {
    return
  }

  console.info(`[Logger]`, ...message)
}

export const isDev = () => process.env.NODE_ENV === 'development'

export type TIpfs = `ipfs://${string}`
export const ipfsGateway = 'ipfs.io';

export const isIpfsId = (resource: string): resource is TIpfs => resource?.startsWith('Qm') && resource?.length === 46

export const buildIpfsResource = (resource: string, isStrict: boolean = false) => {
  if(resource === undefined) return "/images/gemspump-logo-bigger.svg";
  
  // Check if it's already a valid HTTP/HTTPS URL
  if (resource.startsWith('https://') || resource.startsWith('http://')) {
    // Handle specific IPFS gateways
    if (resource.startsWith('https://ipfs.io/')) {
      return resource.replace('https://ipfs.io/', `https://${ipfsGateway}/`)
    } else if (resource.startsWith('https://blue-tired-perch-217.mypinata.cloud/ipfs/')) {
      return `https://${ipfsGateway}/ipfs/${resource.slice(-46)}`
    } else {
      // Return the URL as-is if it's already a valid URL
      return resource
    }
  }
  
  // Handle IPFS protocol URLs
  if (resource.startsWith('ipfs://')) {
    return resource.replace('ipfs://', `https://${ipfsGateway}/ipfs/`)
  }
  
  // Check if it's an IPFS hash
  if (isIpfsId(resource.slice(-46))) {
    return `https://${ipfsGateway}/ipfs/${resource}`
  }
  
  // If it's not IPFS but not a URL either, return fallback or original based on isStrict
  return isStrict ? '/images/gemspump-logo-bigger.svg' : resource
}

const isPulsechain = (chainId: number) => chainId === pulsechain.id

const getExpirationDate = (token: string): number => {
  if (!token) {
    return 0
  }

  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)?.exp * 1000
}

export const isJwtExpired = (token: string) =>
  Date.now() >= getExpirationDate(token) - /** 3 days */ 3 * 60 * 60 * 24 * 1000

const generateUUID = () => uuidv4()

export const catchable = async <T, TError = any>(
  reflected: (...p: any) => Promise<T>,
  onError?: (error: TError) => void,
  handleIsLoading?: (isLoading: boolean) => void
): Promise<T | undefined> => {
  handleIsLoading?.(true)
  let result: T | undefined

  try {
    result = await reflected()
  } catch (error: Error | any) {
    console.error(error)

    onError?.(error)
  } finally {
    handleIsLoading?.(false)
  }

  return result
}

export function toFixedTrim(data: number, decimalPlaces: number) {
  return parseFloat(data?.toFixed(decimalPlaces)).toString()
}

export const formatFlr = (value: number) => {
  if (!value) {
    return 0
  } else if (isNaN(value)) {
    return 0
  }

  if (value > 1000) {
    return toFixedTrim(value, 0)
  } else if (value > 100) {
    return toFixedTrim(value, 2)
  } else if (value > 10) {
    return toFixedTrim(value, 1)
  } else if (value > 1) {
    return toFixedTrim(value, 1)
  } else if (value < 1 && `${value}`.length > 9) {
    return `~${toFixedTrim(value, 7)}`
  }
  return value
}

const nFormatter = (number: number | string, digits: number = 0) => {
  const _number = Number(number)

  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  const item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return _number >= item.value
    })
  return item ? (_number / item.value)?.toFixed(digits).replace(rx, '$1') + item.symbol : '0'
}

type UniqueByKey<T> = (array: T[], key: keyof T) => T[]

export const removeDuplicatesByKey: UniqueByKey<any> = (array, key) => {
  return array.filter((obj1, i, arr) => arr.findIndex(obj2 => obj2[key] === obj1[key]) === i)
}



export { generateUUID, isPulsechain, nFormatter }



export const chainIdToName = (chainId: number): string => {
  switch (chainId) {
    case 14:
      return 'flare'
    case 369:
      return 'pulsechain'
    default:
      return 'unknown'
  }
}

export const chainNameToId = (name: string): number => {
  switch (name.toLowerCase()) {
    case 'flare':
      return 14;
    case 'pulsechain':
      return 369;
    default:
      return -1;
  }
}

